'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';

import dynamic from 'next/dynamic';

import ConfigPanel from './components/ConfigPanel';
import ResetModal from './components/ResetModal';
import { findInheritCountryFilterField } from './utils/filterParams';

const PreviewPanel = dynamic(() => import('./components/PreviewPanel'), {
    ssr: false,
});

import { useChartConfig } from './hooks/useChartConfig';
import { useDatasets } from './hooks/useDatasets';
import { usePreviewData } from './hooks/usePreviewData';
import { configToWagtail } from './utils/configToWagtail';
import { chartBlockFromExport } from '../../chartRenderer/chartBlockAdapter';

import s from './GraphBuilder.module.scss';

const GraphBuilder = () => {
    const [showRestorePrompt, setShowRestorePrompt] = useState(false);
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const {
        config,
        updateConfig,
        resetConfig,
        importConfig,
        hasSavedConfig,
        restoreSavedConfig,
        dismissSavedConfig,
        addSeries,
        removeSeries,
        duplicateSeries,
        updateSeries,
        reorderSeries,
        wagtailSource,
        clearWagtailSource,
    } = useChartConfig();

    const selectedDataset = config.series?.[0]?.dataSource?.value;
    // The preview deliberately renders the exported chart block, rather than
    // its editor config. This makes the preview exercise the same persisted
    // representation that the published renderer receives.
    const chartBlock = useMemo(() => {
        const exported = configToWagtail(config);
        return exported.success ? chartBlockFromExport(exported.json) : null;
    }, [config]);

    const {
        datasets,
        datasetFields,
        filterableFields,
        loadingDatasets,
        loadingFields,
        fetchColumnValues,
        datasetsError,
        fieldsError,
    } = useDatasets(selectedDataset);

    const {
        previewData,
        rowCount,
        totalCount,
        loading: previewLoading,
        error: previewError,
        retry: retryPreview,
        hasInheritCountryFilter,
    } = usePreviewData(config, chartBlock);

    useEffect(() => {
        if (hasSavedConfig) {
            setShowRestorePrompt(true);
        }
    }, [hasSavedConfig]);

    const handleRestore = useCallback(() => {
        restoreSavedConfig();
        setShowRestorePrompt(false);
    }, [restoreSavedConfig]);

    const handleDismissRestore = useCallback(() => {
        dismissSavedConfig();
        setShowRestorePrompt(false);
    }, [dismissSavedConfig]);

    const handleReset = useCallback(() => setResetModalOpen(true), []);

    const handleConfirmReset = useCallback(() => {
        resetConfig();
        setResetModalOpen(false);
    }, [resetConfig]);

    const handleCancelReset = useCallback(() => setResetModalOpen(false), []);

    const handleDatasetChange = useCallback(
        (dataset) => {
            updateConfig((prev) => ({
                ...prev,
                series: prev.series.map((s) => ({
                    ...s,
                    dataSource: { type: 'dataset', value: dataset },
                })),
            }));
        },
        [updateConfig]
    );

    const handlePreviewModeChange = useCallback(
        (mode) => {
            updateConfig({ previewMode: mode });
        },
        [updateConfig]
    );

    // "Inherit country code" filters resolve from the page's country when
    // published; the builder has no page, so offer the field's values as
    // stand-in countries for the preview.
    const inheritFilterField = useMemo(
        () => findInheritCountryFilterField(config),
        [config.series]
    );
    const [inheritCountryOptions, setInheritCountryOptions] = useState([]);

    useEffect(() => {
        if (!inheritFilterField || !selectedDataset) {
            setInheritCountryOptions([]);
            return undefined;
        }
        let cancelled = false;
        fetchColumnValues(selectedDataset, inheritFilterField).then(
            (values) => {
                if (!cancelled) setInheritCountryOptions(values || []);
            }
        );
        return () => {
            cancelled = true;
        };
    }, [inheritFilterField, selectedDataset, fetchColumnValues]);

    const handleInheritCountryChange = useCallback(
        (countryCode) => {
            updateConfig({ previewInheritCountry: countryCode || null });
        },
        [updateConfig]
    );

    return (
        <div className={s.Root}>
            {showRestorePrompt && (
                <div className={s.RestorePrompt}>
                    <div className={s.RestorePromptContent}>
                        <p>
                            You have unsaved configuration from a previous
                            session.
                        </p>
                        <div className={s.RestorePromptActions}>
                            <button
                                onClick={handleRestore}
                                className={s.RestoreButton}>
                                Restore
                            </button>
                            <button
                                onClick={handleDismissRestore}
                                className={s.DismissButton}>
                                Start Fresh
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className={s.Container}>
                <div className={s.ConfigPanel}>
                    <ConfigPanel
                        config={config}
                        updateConfig={updateConfig}
                        onReset={handleReset}
                        onImport={importConfig}
                        datasets={datasets}
                        datasetFields={datasetFields}
                        filterableFields={filterableFields}
                        currentDataset={selectedDataset}
                        loadingDatasets={loadingDatasets}
                        loadingFields={loadingFields}
                        onDatasetChange={handleDatasetChange}
                        datasetsError={datasetsError}
                        fieldsError={fieldsError}
                        addSeries={addSeries}
                        removeSeries={removeSeries}
                        duplicateSeries={duplicateSeries}
                        updateSeries={updateSeries}
                        reorderSeries={reorderSeries}
                        fetchColumnValues={fetchColumnValues}
                        wagtailSource={wagtailSource}
                        onClearWagtailSource={clearWagtailSource}
                    />
                </div>
                <div className={s.PreviewPanel}>
                    <PreviewPanel
                        config={config}
                        chartBlock={chartBlock}
                        previewData={previewData}
                        rowCount={rowCount}
                        totalCount={totalCount}
                        loading={previewLoading}
                        error={previewError}
                        onRetry={retryPreview}
                        previewMode={config.previewMode || 'full'}
                        onPreviewModeChange={handlePreviewModeChange}
                        hasInheritCountryFilter={hasInheritCountryFilter}
                        inheritCountry={config.previewInheritCountry || ''}
                        inheritCountryOptions={inheritCountryOptions}
                        onInheritCountryChange={handleInheritCountryChange}
                    />
                </div>
            </div>

            <ResetModal
                isOpen={resetModalOpen}
                onConfirm={handleConfirmReset}
                onCancel={handleCancelReset}
            />
        </div>
    );
};

export default GraphBuilder;
